import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../index";
import { BuildModeSuggestionType } from "./types";

import { BuildModeSuggestions } from "./BuildModeSuggestions";

// Re-export for external use
export { BuildModeSuggestionType };

/**
 * Tool parameters for showing build mode suggestion pills
 * Supports both conversational suggestions and auth creation functionality
 */
export const BuildModeSuggestionsParamsSchema = z
  .object({
    suggestions: z
      .array(z.nativeEnum(BuildModeSuggestionType))
      .default([BuildModeSuggestionType.SETUP_ZAPS]),
    selected_api: z
      .string()
      .optional()
      .describe(
        "The API identifier to create authentication for (e.g., 'GoogleCalendarCLIAPI@1.5.0' or 'gmail'). REQUIRED when suggestions include CREATE_AUTH.",
      ),
    displayMessage: z
      .string()
      .optional()
      .describe(
        "Short 3-5 word friendly message describing what you're doing, like 'Showing next steps...' or 'Ready to help...'",
      ),
  })
  .refine(
    (data) => {
      // If CREATE_AUTH is in suggestions, selected_api must be provided
      const hasCreateAuth = data.suggestions.includes(
        BuildModeSuggestionType.CREATE_AUTH,
      );
      return !hasCreateAuth || (hasCreateAuth && data.selected_api);
    },
    {
      message:
        "selected_api is required when suggestions include CREATE_AUTH (🔌 Create new App Connection)",
      path: ["selected_api"],
    },
  );

export type BuildModeSuggestionsResult = {
  success: boolean;
  message: string;
};

/**
 * Build mode suggestions client tool for Canvas AI toolkit
 * Shows interactive suggestion pills that users can click to continue the conversation
 * Also supports auth creation functionality when selected_api is provided
 */
export const showBuildModeSuggestionsAtom = atom<
  JotaiTool<z.infer<typeof BuildModeSuggestionsParamsSchema>>
>(() => ({
  name: "show_build_mode_suggestions",
  description:
    "Show interactive suggestion pills for common build mode actions, including field mapping setup and app authentication creation. IMPORTANT: If you include CREATE_AUTH in suggestions, you MUST provide selected_api parameter.",
  parameters: BuildModeSuggestionsParamsSchema,
  component: BuildModeSuggestions,
  execute: atom(null, async (_get, _set, { args }) => {
    const { suggestions } = args;
    return {
      success: true,
      message: `Showing ${suggestions.length} build mode suggestion pill(s)`,
    };
  }),
}));
